import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Plus, Search, Phone, Mail, Home, Database, RefreshCw, EyeOff, Undo2, StickyNote, Pencil, XCircle, UserCheck, CheckCircle2, FileSignature } from "lucide-react";
import toast from "react-hot-toast";
import moment from "moment";
import { getLeads, markSellerLeadListed, setSellerLeadAction } from "../../../api/lead";
import { getAgents } from "../../../api/agent";
import InfiniteScrollSelect from "../../../components/common/InfiniteScrollSelect";
import PageHeader from "../../../components/shared/PageHeader";
import AgencyScopeSwitch, {
  useAgencyScope,
  useExtraAgents,
  IncludeAgentsPicker,
} from "../../../components/common/AgencyScopeSwitch";
import AddSellerLeadModal from "../components/AddSellerLeadModal";
import PublicMatchBadge from "../../properties/components/PublicMatchBadge";
import PublicPropertyLink from "../../properties/components/PublicPropertyLink";
import PropertyProfileLink from "../../properties/components/PropertyProfileLink";
import AddToDatabaseModal from "../components/AddToDatabaseModal";
import AssignLeadModal from "../components/AssignLeadModal";
import LeadEditModal from "../components/LeadEditModal";
import LeadNotesModal from "../components/LeadNotesModal";
import IgnoreLeadReasonModal, { IGNORE_REASONS } from "../components/IgnoreLeadReasonModal";
import ReassignIgnoredLeadModal from "../components/ReassignIgnoredLeadModal";
import { hasAdminAccess, hasSuperAdminAccess } from "../../../utils/roleAccess";
import CreateProposalModal from "../../prospecting/components/CreateProposalModal";
import { parseAuAddress } from "../utils/auAddress";
import { LOST_REASONS, withNote } from "../constants/lostReasons";
import LostReasonPicker from "../components/LostReasonPicker";
import ContactLink from "../../contacts/components/ContactLink";
import RentalLeadPill from "../components/RentalLeadPill";

// Human labels for Lead.SOURCE_CHOICES (lead_management/models/leads.py).
const SOURCE_LABELS = {
  social_media: "Social Media",
  social: "Social Media",
  rea: "REA.com.au",
  realestate: "REA.com.au",
  domain: "Domain.com.au",
  meta: "Meta (Facebook/Instagram)",
  website: "Website",
  referral: "Referral",
  cold_call: "Cold Call",
  ofi: "Open For Inspection",
  chatbot: "Chatbot",
  buyer_portal: "Buyer Portal",
  form: "Form",
  direct: "Direct Contact",
  voiceCaller: "Voice Caller",
  database: "Database",
  relisting: "Re-listing Opportunity",
  google: "Google",
  ai_chat: "ChatGPT / Claude / Gemini / Grok",
  other: "Other",
};

const TABS = [
  { key: "off_market", label: "Off Market Leads" },
  { key: "on_market", label: "On Market Leads" },
  { key: "actioned", label: "Actioned" },
];

// Lead.SELLER_ACTION_CHOICES — what the agent did with the lead.
const ACTION_LABELS = {
  appraisal: "Added as Appraisal",
  database: "Added to Database",
  ignored: "Ignored",
  lost: "Lost",
  listed: "Listed",
};

// Christian Gravias asked for the reason AND the note the agent typed to show
// on the Actioned tab, not just the "Ignored" pill (Monday 13058325657,
// 2026-09-17). Ignored leads carry ignore_reason/ignore_notes, lost leads carry
// lost_reason, so both read back here without opening Analytics.
const IGNORE_REASON_LABELS = Object.fromEntries(IGNORE_REASONS.map((r) => [r.value, r.label]));
const LOST_REASON_LABELS = Object.fromEntries(LOST_REASONS.map((r) => [r.value, r.label]));

const actionedReasonLabel = (lead) => {
  if (lead.seller_action === "ignored") return IGNORE_REASON_LABELS[lead.ignore_reason] || "";
  if (lead.seller_action === "lost") return withNote(LOST_REASON_LABELS[lead.lost_reason] || "", lead.lost_reason_note);
  return "";
};

// "Mark as Lost" reasons — shared with the pipeline card / Edit Appraisal modal.

/**
 * Seller Leads — where the seller leads coming in from Meta, REA and Domain land
 * so an agent can work them: contact the person, then either turn the lead into an
 * appraisal or drop it into their Database. Admins can also hand a lead to another
 * agent from here (Christian Gravias, Monday 12817261831, 2026-08-17).
 *
 * This is what the Dashboard's "Off Market Leads" / "On Market Leads" widgets now
 * open, instead of dropping the agent into the appraisal pipeline board.
 *
 * It is a HOLDING AREA: the moment a lead is actioned — added as an appraisal,
 * added to the Database, or ignored — it leaves the two market tabs and the
 * dashboard widgets, so the list is only ever what still needs working
 * (Christian Gravias, 2026-08-18). Nothing is deleted; actioned leads sit on the
 * third tab and can be put back with Restore.
 */
// Seller leads only carry one free-text address blob (Lead.property_address);
// the Add Appraisal form makes Suburb, State and Postcode their own required
// fields. parseAuAddress (../utils/auAddress) splits the blob so those fields
// carry over instead of the agent retyping them (CRM-2026-00042, #12985966294).

const SellerLeadsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useSelector((state) => state.auth || {});
  // "Please make leads info editable for admin" (Christian Gravias, Monday
  // 12497182031, 2026-08-22). Admin only, as asked: an agent working their own
  // leads should not be quietly rewriting the name and number the enquiry
  // arrived with, because that is the record of what the person actually sent.
  const isAdmin = hasAdminAccess(user);
  // Only a super admin can re-assign an ignored lead (#12984261525 revision).
  const canReassignIgnored = hasSuperAdminAccess(user);
  const [reassignLead, setReassignLead] = useState(null);

  const tab = TABS.some((t) => t.key === searchParams.get("market"))
    ? searchParams.get("market")
    : "off_market";
  const isActionedTab = tab === "actioned";
  // The market bucket the row actions should use. The Actioned tab holds both
  // buckets, so any prefill from there falls back to off_market.
  const market = isActionedTab ? "off_market" : tab;

  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({ off_market: 0, on_market: 0, actioned: 0 });
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  // The lead the "Add to Database" picker is open for.
  const [dbLead, setDbLead] = useState(null);
  // The lead whose notes thread is open. Notes belong on the lead itself, so an
  // agent can write up a call before deciding whether the lead becomes an
  // appraisal or a Database record (Christian Gravias, 2026-08-21).
  const [notesLead, setNotesLead] = useState(null);
  // Leads the agent has clicked "Called" on this session, so the button reads
  // as done without needing a reload (the lead stays in the holding area).
  const [calledIds, setCalledIds] = useState(() => new Set());
  // Contacted = called this session OR on any earlier one. last_contact_date is
  // only ever written by the Called action, so it is the persisted form of the
  // same fact — without it the green state vanished on reload (Monday 13156688648).
  const isContacted = (lead) => calledIds.has(lead.id) || Boolean(lead.last_contact_date);
  // The lead whose details are being corrected by hand, and the lead being
  // handed to an agent (with the agent picked in the row's dropdown, so the
  // modal opens on the right person).
  const [editLead, setEditLead] = useState(null);
  // DOMA Proposals: admin-only "Create proposal" from an appraisal (seller lead).
  const [proposalLead, setProposalLead] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [lostMenuFor, setLostMenuFor] = useState(null); // lead id whose "Lost reason" menu is open
  const [ignoreTarget, setIgnoreTarget] = useState(null); // lead pending the "why ignore?" modal

  // Whose seller leads. Same rule and the same controls as Buyer Enquiries &
  // Offers - an admin gets their own desk by default and asks for the office,
  // rather than being handed it (Simon Abbott, 3 Sep 2026: "I'm getting other
  // agents ... leads, appraisals etc in my CRM").
  const {
    agencyWide,
    setAgencyWide,
    canSwitch: canScope,
    canIncludeAgents,
  } = useAgencyScope("sellerLeads");
  const { agentIds: extraAgentIds, setAgentIds: setExtraAgentIds, toggleAgentId } =
    useExtraAgents("sellerLeads", canIncludeAgents);

  const scopeParams = useMemo(() => {
    if (!canScope && !canIncludeAgents) return {};
    if (agencyWide) return { agency_wide: "true" };
    return extraAgentIds.length
      ? { agency_wide: "false", agents: extraAgentIds.join(",") }
      : { agency_wide: "false" };
  }, [canScope, canIncludeAgents, agencyWide, extraAgentIds]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // All three lists every time: the tab chips show a count, and the lists are
      // small enough (seller leads, not the whole buyer holding area) that one
      // extra call is cheaper than a second round trip on every tab click.
      const [offRes, onRes, actionedRes] = await Promise.all([
        getLeads({ type: "seller", market: "off_market", page_size: 200, ...scopeParams }),
        getLeads({ type: "seller", market: "on_market", page_size: 200, ...scopeParams }),
        getLeads({ type: "seller", actioned: "true", page_size: 200, ...scopeParams }),
      ]);
      const off = offRes.data?.results || [];
      const on = onRes.data?.results || [];
      const actioned = actionedRes.data?.results || [];
      setCounts({
        off_market: offRes.data?.count ?? off.length,
        on_market: onRes.data?.count ?? on.length,
        actioned: actionedRes.data?.count ?? actioned.length,
      });
      setLeads(tab === "actioned" ? actioned : tab === "on_market" ? on : off);
    } catch (err) {
      console.error("Error loading seller leads:", err);
      toast.error("Failed to load seller leads.");
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [tab, scopeParams]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    // Agents only — the assign picker (and the include-agents filter) never
    // want admin / super_admin / dev accounts.
    getAgents({ page_size: 200, role: "agent" })
      .then((res) => {
        const data = res?.data ?? {};
        setAgents(data.results ?? (Array.isArray(data) ? data : []));
      })
      .catch(() => setAgents([]));
  }, []);

  // Server-side agent search for the per-row assign typeahead.
  const fetchAgentOptions = useCallback(async (page, pageSize, search = "") => {
    const res = await getAgents({ page, page_size: pageSize, search, role: "agent" });
    const data = res?.data ?? {};
    return {
      results: data.results ?? (Array.isArray(data) ? data : []),
      next: Boolean(data.next),
    };
  }, []);

  const visibleLeads = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((l) =>
      [l.name, l.full_name, l.email, l.phone, l.property_address]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [leads, search]);

  const switchTab = (key) => {
    setSearchParams({ market: key });
    setSearch("");
  };

  const addressFor = (lead) => lead.property_address || lead.owner_subject_address || "";

  // The lead-ingest API falls back to the email address (then the phone number)
  // when the feed sent no name at all — InboundLead.display_name — which is why
  // rows off the "Genneva Appraisal Form" and "Tomahawk Appraisal Form" Zaps show
  // an email where the person's name should be. Worth flagging to an admin, since
  // they are the one who can type the real name in.
  const nameMissing = (lead) => {
    const name = (lead.name || lead.full_name || "").trim();
    if (!name) return true;
    return name.includes("@") || name === (lead.phone || "").trim();
  };

  const notesOf = (lead) => (Array.isArray(lead?.agent_notes) ? lead.agent_notes : []);
  const lastNoteOf = (lead) => notesOf(lead)[notesOf(lead).length - 1] || null;

  // A note never actions the lead, so the row stays where it is; only its note
  // count and the last-note line under the name change.
  const handleNoteSaved = (updated) => {
    setLeads((prev) => prev.map((l) => (l.id === updated.id ? { ...l, agent_notes: updated.agent_notes } : l)));
    setNotesLead((prev) => (prev && prev.id === updated.id ? { ...prev, agent_notes: updated.agent_notes } : prev));
  };

  // Take the row off the tab the agent is looking at and move its count across,
  // so the holding area empties as they work it without waiting for a reload.
  const removeRow = (lead, movedTo) => {
    setLeads((prev) => prev.filter((l) => l.id !== lead.id));
    setCounts((prev) => ({
      ...prev,
      [tab]: Math.max(0, (prev[tab] || 0) - 1),
      [movedTo]: (prev[movedTo] || 0) + 1,
    }));
  };

  // Assign the lead to another agent. Christian collects the social-media leads
  // centrally and then hands each one to whoever should chase it.
  //
  // Picking an agent no longer saves straight away: it opens the hand-over
  // modal, so there is somewhere to write the agent a note first (Christian
  // Gravias, Monday 12497182031, 2026-08-22 — "add a button which allows me to
  // write a note to the agent before I send the lead over to them"). The note is
  // optional, so this is one extra click, not a new step.
  //
  // The <select> stays bound to lead.agent and is never set from here, so
  // closing the modal without sending snaps it back to who the lead is really
  // with, rather than leaving the row showing an assignment that never happened.
  const openAssign = (lead, agentId) => setAssigning({ lead, agentId });

  // The lead came back from the modal already saved. A hand-over to somebody
  // else takes the row off this page the same way Ignore and Lost do — the
  // whole gesture is "it is not mine any more" (Monday 13146495144 /
  // CRM-2026-00078: a reassigned lead sat on the sender's list). The row only
  // stays when this view would still show it after a reload: it is mine, or
  // I am looking at the whole agency, or the new agent is one I folded in.
  const handleAssigned = (updated) => {
    const newAgentId = Number(updated.agent);
    // Unassigning sends the lead back to the pool: only its creator and the
    // admin tier (who see unclaimed leads) would find it here after a reload.
    const stillVisible = updated.agent
      ? newAgentId === Number(user?.id) ||
        agencyWide ||
        extraAgentIds.includes(newAgentId)
      : agencyWide ||
        canIncludeAgents ||
        Number(updated.created_by_user) === Number(user?.id);
    if (!stillVisible) {
      setLeads((prev) => prev.filter((l) => l.id !== updated.id));
      setCounts((prev) => ({ ...prev, [tab]: Math.max(0, (prev[tab] || 0) - 1) }));
      return;
    }
    setLeads((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
  };

  // Details corrected by hand, which never actions the lead, so the row stays.
  const handleLeadEdited = (updated) => {
    setLeads((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
  };

  // Hand the lead to the Add Appraisal form with the person and address already
  // filled in. Uses the same prefill contract as the Database's "Convert to
  // appraisal" (AddPropertyPage.js PREFILL_FIELD_MAP). The lead is only marked
  // actioned once the appraisal is actually saved (AddPropertyPage.js's isSuccess
  // callback, via sellerLeadId) - not on click - so an agent who opens the form
  // and then leaves without submitting finds the lead still sitting in the
  // holding area instead of vanished with no appraisal to show for it (Christian
  // Gravias, Monday 12887036288, 2026-08-25).
  const handleAddAsAppraisal = (lead) => {
    const parsed = parseAuAddress(addressFor(lead));
    navigate("/properties/add-property", {
      state: {
        listingType: "appraisal",
        sellerLeadId: lead.id,
        prefill: {
          address: parsed.address,
          suburb: parsed.suburb,
          state: parsed.state,
          postal_code: parsed.postal_code,
          owner_name: lead.name || lead.full_name || "",
          owner_contact: lead.phone || "",
          owner_email: lead.email || "",
          appraisal_market_type: lead.market === "on_market" ? "on_market" : market,
        },
      },
    });
  };

  // "Listed" — one click. The row leaves the list, the property becomes a
  // draft listing (On/Off Market per the lead) in the agent's My Listings and
  // Collings Properties, and Listings Won counts it. It used to open the whole
  // Add Property form, which is also how a second profile got keyed in for a
  // house already on file (Christian Gravias, Monday 13177615952). Only a lead
  // with no address still needs the form, so that case falls back to it.
  const openListingForm = (lead) => {
    const parsed = parseAuAddress(addressFor(lead));
    const onMarket = lead.market === "on_market" || market === "on_market";
    navigate("/properties/add-property", {
      state: {
        listingType: onMarket ? "onmarket" : "offmarket",
        sellerLeadId: lead.id,
        prefill: {
          address: parsed.address,
          suburb: parsed.suburb,
          state: parsed.state,
          postal_code: parsed.postal_code,
          owner_name: lead.name || lead.full_name || "",
          owner_contact: lead.phone || "",
          owner_email: lead.email || "",
        },
      },
    });
  };

  const handleMarkAsListed = async (lead) => {
    const parsed = parseAuAddress(addressFor(lead));
    setBusyId(lead.id);
    try {
      const { data } = await markSellerLeadListed(lead.id, {
        address: parsed.address,
        suburb: parsed.suburb,
        state: parsed.state,
        postal_code: parsed.postal_code,
      });
      // From the Actioned tab (an appraisal that has now listed) the row stays
      // put and just reads "Listed" (Genneva Smarrelli, CRM-2026-00108).
      if (isActionedTab) {
        setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, ...(data?.lead || { seller_action: "listed" }) } : l)));
      } else {
        removeRow(lead, "actioned");
      }
      const where = data?.property?.address ? `${data.property.address} moved` : "Moved";
      toast.success(`${where} to My Listings as a draft.`);
    } catch (err) {
      const body = err?.response?.data;
      if (body?.code === "no_address") {
        openListingForm(lead);
        return;
      }
      toast.error(body?.detail || "Failed to mark as listed.");
    } finally {
      setBusyId(null);
    }
  };

  // Drop the lead into the agent's Database. The button opens a picker first:
  // Christian Gravias (2026-08-19) wanted to choose which part of the Database
  // and which category the lead is filed into, rather than every lead landing in
  // the Owners grid with no category. AddToDatabaseModal writes the record (the
  // prospecting serializer mirrors it into a CRM Contact for free) and hands the
  // lead back here to be marked off the holding area.
  const handleDatabaseSaved = async (lead) => {
    // Already actioned (an appraisal also being filed into a Database list):
    // keep "Added as Appraisal" as what happened, the Database record is extra.
    if (lead.seller_action) return;
    setBusyId(lead.id);
    try {
      await setSellerLeadAction(lead.id, "database");
      removeRow(lead, "actioned");
    } catch (markErr) {
      // The record is already saved, so never surface this as a failure — the row
      // just stays on the list.
      console.error("Failed to mark the seller lead as actioned:", markErr);
    } finally {
      setBusyId(null);
    }
  };

  // Not worth chasing. Clears it off the list without deleting the lead, so it
  // still counts as a lead that arrived and can be restored from the Actioned tab.
  const handleIgnore = async (lead, reason, notes) => {
    setBusyId(lead.id);
    try {
      await setSellerLeadAction(lead.id, "ignored", reason, notes);
      removeRow(lead, "actioned");
      toast.success("Lead ignored.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to ignore the lead.");
    } finally {
      setBusyId(null);
    }
  };

  // Mark that the agent has actually called/spoken to the lead. This records a
  // contact for the per-agent stats (Contacted + first-response time) without
  // actioning the lead, so it STAYS in the holding area for follow-up
  // (Christian Gravias, revision #12975449949).
  const handleCalled = async (lead) => {
    setBusyId(lead.id);
    try {
      await setSellerLeadAction(lead.id, "called");
      setCalledIds((prev) => new Set(prev).add(lead.id));
      toast.success("Marked as contacted.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to mark as contacted.");
    } finally {
      setBusyId(null);
    }
  };

  // Mark a GENUINE lead as Lost with a reason (distinct from Ignore).
  const handleMarkLost = async (lead, reason, note) => {
    setLostMenuFor(null);
    setBusyId(lead.id);
    try {
      await setSellerLeadAction(lead.id, "lost", reason, note);
      removeRow(lead, "actioned");
      toast.success("Lead marked as lost.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to mark the lead as lost.");
    } finally {
      setBusyId(null);
    }
  };

  // Put an actioned lead back into its market tab.
  const handleRestore = async (lead) => {
    setBusyId(lead.id);
    try {
      await setSellerLeadAction(lead.id, "clear");
      removeRow(lead, lead.market === "on_market" ? "on_market" : "off_market");
      toast.success("Lead restored.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to restore the lead.");
    } finally {
      setBusyId(null);
    }
  };

  const emptyMessage = isActionedTab
    ? "No actioned leads yet."
    : `No ${tab === "on_market" ? "on market" : "off market"} leads yet.`;

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-white min-h-screen">
      <PageHeader
        title="Seller Leads"
        subtitle="Seller leads from Meta, REA and Domain. Contact them and write up the call in Notes, then add them as an appraisal, add them to your Database, or ignore them. Once a lead is actioned it leaves the list."
        action={
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {(canScope || canIncludeAgents) && (
              <>
                {canScope && (
                  <AgencyScopeSwitch
                    agencyWide={agencyWide}
                    onChange={setAgencyWide}
                    mineLabel="My leads"
                    agencyLabel="All agents"
                  />
                )}
                {!agencyWide && canIncludeAgents && (
                  <IncludeAgentsPicker
                    agents={agents}
                    selectedIds={extraAgentIds}
                    onToggle={toggleAgentId}
                    onClear={() => setExtraAgentIds([])}
                    currentUserId={user?.id}
                  />
                )}
              </>
            )}
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="bg-[#55C5D0] hover:bg-[#45b5c0] text-white px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg font-bold flex items-center justify-center gap-2 text-xs sm:text-sm w-full sm:w-auto"
            >
              <Plus className="w-4 h-4" />
              Add Lead
            </button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-2 mb-4">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => switchTab(t.key)}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
              tab === t.key
                ? "bg-[#55C5D0] text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            ({counts[t.key] || 0}) {t.label}
          </button>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, phone, email or address..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
          />
        </div>
        <button
          type="button"
          onClick={load}
          className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 flex items-center justify-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="py-16 text-center text-gray-500 text-sm">Loading seller leads...</div>
      ) : visibleLeads.length === 0 ? (
        <div className="py-16 text-center text-gray-500 text-sm border border-dashed border-gray-200 rounded-xl">
          {emptyMessage}
        </div>
      ) : (
        <div className="border border-gray-200 rounded-xl overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-left font-semibold px-4 py-3">Lead</th>
                <th className="text-left font-semibold px-4 py-3">Property</th>
                <th className="text-left font-semibold px-4 py-3">Source</th>
                <th className="text-left font-semibold px-4 py-3">Selling Time Frame</th>
                <th className="text-left font-semibold px-4 py-3">Added</th>
                <th className="text-left font-semibold px-4 py-3">
                  {isActionedTab ? "What happened" : "Assign an agent"}
                </th>
                <th className="text-left font-semibold px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visibleLeads.map((lead) => (
                <tr key={lead.id} className="hover:bg-gray-50 align-top">
                  <td className="px-4 py-3">
                    <ContactLink
                      identity={{
                        // Seller leads (e.g. Meta/Facebook) often have a phone
                        // but no email, so lead first on the linked contact_id
                        // and only fall back to email — otherwise the name has
                        // nothing to resolve to and the click can't open a
                        // profile (Michael Piccoli, Monday 13090427952).
                        contactId: lead.contact_id,
                        buyerId: lead.buyer_id,
                        email: lead.email,
                        name: lead.name || lead.full_name,
                        phone: lead.phone,
                      }}
                      className="font-bold text-gray-900 text-left hover:text-[#3a99a3] hover:underline"
                    >
                      {lead.name || lead.full_name || "No Name"}
                    </ContactLink>
                    {isAdmin && nameMissing(lead) && (
                      <button
                        type="button"
                        onClick={() => setEditLead(lead)}
                        className="mt-0.5 text-[11px] font-semibold text-amber-600 hover:underline"
                      >
                        No name came through — add one
                      </button>
                    )}
                    {["unmatched", "needs_review"].includes(lead.public_match_status) && (
                      <div className="mt-0.5">
                        <PublicMatchBadge status={lead.public_match_status} />
                      </div>
                    )}
                    <div className="flex flex-wrap gap-3 mt-1 text-xs">
                      {lead.phone && (
                        <a href={`tel:${lead.phone}`} className="text-[#55C5D0] font-medium flex items-center gap-1 hover:underline">
                          <Phone className="w-3 h-3" /> {lead.phone}
                        </a>
                      )}
                      {lead.email && (
                        <a href={`mailto:${lead.email}`} className="text-[#55C5D0] font-medium flex items-center gap-1 hover:underline">
                          <Mail className="w-3 h-3" /> {lead.email}
                        </a>
                      )}
                    </div>
                    {/* The last thing that happened on the lead, on the row, so
                        the agent can see who has already been called without
                        opening every one. */}
                    {lastNoteOf(lead) && (
                      <div className="mt-1.5 text-xs text-gray-500 max-w-[280px]">
                        <span className="font-semibold text-gray-400">Last note:</span>{" "}
                        <span className="line-clamp-2">{lastNoteOf(lead).content}</span>
                      </div>
                    )}
                  </td>
                  {/* Meta's lead forms only pass on the answers they actually
                      ask for, and none of the live Collings forms asks for an
                      address, so this cell is empty on most Facebook leads. An
                      admin can type it in from here (Christian Gravias, Monday
                      12497182031). */}
                  <td className="px-4 py-3 text-gray-700">
                    {addressFor(lead) ? (
                      <>
                        {addressFor(lead)}
                        {(lead.database_record || lead.property_profile) && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            <PublicPropertyLink record={lead.database_record} />
                            <PropertyProfileLink profile={lead.property_profile} />
                          </div>
                        )}
                      </>
                    ) : isAdmin ? (
                      <button
                        type="button"
                        onClick={() => setEditLead(lead)}
                        className="text-xs font-semibold text-amber-600 hover:underline"
                      >
                        Add address
                      </button>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {SOURCE_LABELS[lead.source] || lead.source || "—"}
                    <RentalLeadPill transactionType={lead.transaction_type} className="ml-1.5" />
                  </td>
                  {/* The seller's "when are you thinking of selling" answer from
                      the enquiry form (Michael Piccoli, Monday 13090744225).
                      Stored on timeline / owner_timeline; shown verbatim. */}
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                    {lead.timeline || lead.owner_timeline || "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                    {lead.created_at ? moment(lead.created_at).format("DD MMM YYYY") : "—"}
                    {/* The same owner enquired again about the same property, so
                        it went onto this lead instead of a new row (Christian
                        Gravias, CRM-2026-00119). The list is ordered by the
                        latest enquiry, which is why an older date can sit on top. */}
                    {lead.enquiry_count > 1 && (
                      <div className="mt-1">
                        <span
                          title={`Enquired ${lead.enquiry_count} times, most recently ${moment(lead.last_enquired_at).format("DD MMM YYYY, h:mm a")}`}
                          className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200"
                        >
                          Enquired again {moment(lead.last_enquired_at).format("DD MMM")}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {isActionedTab ? (
                      <div className="max-w-[260px]">
                        <span className="inline-block px-2 py-1 rounded-md bg-gray-100 text-gray-700 text-xs font-bold">
                          {ACTION_LABELS[lead.seller_action] || "Actioned"}
                        </span>
                        {actionedReasonLabel(lead) && (
                          <div className="text-xs font-semibold text-gray-700 mt-1">{actionedReasonLabel(lead)}</div>
                        )}
                        {lead.seller_action === "ignored" && lead.ignore_notes && (
                          <div className="text-xs text-gray-600 mt-0.5 whitespace-pre-wrap break-words">
                            {lead.ignore_notes}
                          </div>
                        )}
                        <div className="text-xs text-gray-500 mt-1">
                          {lead.seller_action_by_name ? `by ${lead.seller_action_by_name}` : ""}
                          {lead.seller_action_at ? ` on ${moment(lead.seller_action_at).format("DD MMM YYYY")}` : ""}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <InfiniteScrollSelect
                          value={lead.agent || ""}
                          disabled={busyId === lead.id}
                          onChange={(val) => openAssign(lead, val)}
                          placeholder="Unassigned"
                          fetchOptions={fetchAgentOptions}
                          initialOptions={agents}
                          getLabel={(a) => a.name || a.email}
                          getValue={(a) => a.id}
                          className="min-w-[150px] max-w-[220px] text-xs"
                        />
                        <button
                          type="button"
                          onClick={() => openAssign(lead, lead.agent || "")}
                          className="mt-1 text-[11px] font-semibold text-[#55C5D0] hover:underline"
                        >
                          Send with a note
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {/* Notes come first and are on every tab, including Actioned:
                        the call history is what the agent needs before deciding
                        between an appraisal and the Database, and it should still
                        be readable after they have decided. */}
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setNotesLead(lead)}
                        className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 text-xs font-bold flex items-center gap-1 hover:bg-gray-50"
                      >
                        <StickyNote className="w-3 h-3" />
                        Notes{notesOf(lead).length ? ` (${notesOf(lead).length})` : ""}
                      </button>
                      {/* Admin only, and on every tab including Actioned: a name
                          or an address that never fed through is still worth
                          fixing after the lead has been worked. */}
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => setEditLead(lead)}
                          className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 text-xs font-bold flex items-center gap-1 hover:bg-gray-50"
                        >
                          <Pencil className="w-3 h-3" />
                          Edit
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => setProposalLead(lead)}
                          className="px-3 py-1.5 rounded-lg bg-[#0f8d9b] text-white text-xs font-bold flex items-center gap-1 hover:opacity-90"
                        >
                          <FileSignature className="w-3 h-3" />
                          Create proposal
                        </button>
                      )}
                    </div>
                    {isActionedTab ? (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {canReassignIgnored && lead.seller_action === "ignored" && (
                          <button
                            type="button"
                            onClick={() => setReassignLead(lead)}
                            className="px-3 py-1.5 rounded-lg border border-[#55C5D0]/40 text-[#3f9ba4] text-xs font-bold flex items-center gap-1 hover:bg-[#f0fafb]"
                          >
                            <UserCheck className="w-3 h-3" /> Re-assign
                          </button>
                        )}
                        {/* An appraisal can still be filed into a Database list and
                            marked Listed once it wins (Genneva Smarrelli,
                            CRM-2026-00108). */}
                        {lead.seller_action === "appraisal" && (
                          <>
                            <button
                              type="button"
                              disabled={busyId === lead.id}
                              onClick={() => handleMarkAsListed(lead)}
                              title="Mark as Listed (moves to My Listings as a draft)"
                              className="px-3 py-1.5 rounded-lg border border-[#55C5D0] text-[#2b9aa4] text-xs font-bold flex items-center gap-1 hover:bg-[#eefafb] disabled:opacity-60"
                            >
                              <CheckCircle2 className="w-3 h-3" /> Listed
                            </button>
                            <button
                              type="button"
                              disabled={busyId === lead.id}
                              onClick={() => setDbLead(lead)}
                              className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 text-xs font-bold flex items-center gap-1 hover:bg-gray-50 disabled:opacity-60"
                            >
                              <Database className="w-3 h-3" /> Add to Database
                            </button>
                          </>
                        )}
                        <button
                          type="button"
                          disabled={busyId === lead.id}
                          onClick={() => handleRestore(lead)}
                          className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 text-xs font-bold flex items-center gap-1 hover:bg-gray-50 disabled:opacity-60"
                        >
                          <Undo2 className="w-3 h-3" /> Restore
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {/* Records a contact for the stats and stays in the list.
                            Contacted reads from last_contact_date too, so the green
                            state survives a reload (Monday 13156688648) — that field
                            is only ever written by this button's action. The
                            contacted state skips the disabled dim: it's lit green,
                            not greyed out. */}
                        <button
                          type="button"
                          disabled={busyId === lead.id || isContacted(lead)}
                          onClick={() => handleCalled(lead)}
                          className={`px-3 py-1.5 rounded-lg border text-xs font-bold flex items-center gap-1 ${
                            isContacted(lead)
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                          }`}
                        >
                          <Phone className="w-3 h-3" /> {isContacted(lead) ? "Contacted" : "Called"}
                        </button>
                        <button
                          type="button"
                          disabled={busyId === lead.id}
                          onClick={() => handleAddAsAppraisal(lead)}
                          className="px-3 py-1.5 rounded-lg bg-[#55C5D0] text-white text-xs font-bold flex items-center gap-1 hover:bg-[#45b5c0] disabled:opacity-60"
                        >
                          <Home className="w-3 h-3" /> Add as Appraisal
                        </button>
                        <button
                          type="button"
                          disabled={busyId === lead.id}
                          onClick={() => handleMarkAsListed(lead)}
                          title="Mark as Listed (moves to My Listings as a draft)"
                          className="px-3 py-1.5 rounded-lg border border-[#55C5D0] text-[#2b9aa4] text-xs font-bold flex items-center gap-1 hover:bg-[#eefafb] disabled:opacity-60"
                        >
                          <CheckCircle2 className="w-3 h-3" /> Listed
                        </button>
                        <button
                          type="button"
                          disabled={busyId === lead.id}
                          onClick={() => setDbLead(lead)}
                          className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 text-xs font-bold flex items-center gap-1 hover:bg-gray-50 disabled:opacity-60"
                        >
                          <Database className="w-3 h-3" /> Add to Database
                        </button>
                        <button
                          type="button"
                          disabled={busyId === lead.id}
                          onClick={() => setIgnoreTarget(lead)}
                          className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 text-xs font-bold flex items-center gap-1 hover:bg-gray-50 hover:text-red-600 disabled:opacity-60"
                        >
                          <EyeOff className="w-3 h-3" /> Ignore
                        </button>
                        {/* Mark as Lost — a genuine lead we lost, with a reason.
                            Kept separate from Ignore so lead-quality stays clean. */}
                        <div className="relative">
                          <button
                            type="button"
                            disabled={busyId === lead.id}
                            onClick={() => setLostMenuFor(lostMenuFor === lead.id ? null : lead.id)}
                            className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 text-xs font-bold flex items-center gap-1 hover:bg-gray-50 hover:text-[#c8392f] disabled:opacity-60"
                          >
                            <XCircle className="w-3 h-3" /> Lost
                          </button>
                          {lostMenuFor === lead.id && (
                            <div className="absolute right-0 z-20 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-xl py-1">
                              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-gray-400">Why was it lost?</div>
                              <LostReasonPicker compact onPick={(reason, note) => handleMarkLost(lead, reason, note)} />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AddSellerLeadModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        defaultMarket={market}
        onSaved={load}
      />

      <AddToDatabaseModal
        isOpen={!!dbLead}
        lead={dbLead}
        onClose={() => setDbLead(null)}
        onSaved={handleDatabaseSaved}
      />

      <LeadNotesModal
        isOpen={!!notesLead}
        lead={notesLead}
        onClose={() => setNotesLead(null)}
        onSaved={handleNoteSaved}
      />

      <IgnoreLeadReasonModal
        open={!!ignoreTarget}
        busy={!!ignoreTarget && busyId === ignoreTarget.id}
        onClose={() => setIgnoreTarget(null)}
        onConfirm={(reason, notes) => {
          const lead = ignoreTarget;
          setIgnoreTarget(null);
          if (lead) handleIgnore(lead, reason, notes);
        }}
      />

      <AssignLeadModal
        isOpen={!!assigning}
        lead={assigning?.lead || null}
        agents={agents}
        initialAgentId={assigning?.agentId ?? ""}
        onClose={() => setAssigning(null)}
        onAssigned={handleAssigned}
      />

      <ReassignIgnoredLeadModal
        open={!!reassignLead}
        lead={reassignLead}
        agents={(agents || []).map((a) => ({ id: a.id, name: a.name || a.email })).filter((a) => a.id && a.name)}
        onClose={() => setReassignLead(null)}
        onReassigned={(lead) => {
          // No longer ignored, so it leaves the Actioned tab.
          setLeads((prev) => prev.filter((l) => l.id !== lead.id));
        }}
      />

      <LeadEditModal
        isOpen={!!editLead}
        lead={editLead}
        onClose={() => setEditLead(null)}
        onSaved={handleLeadEdited}
      />

      {proposalLead && (
        <CreateProposalModal
          prefill={{
            lead_id: proposalLead.id,
            full_address: addressFor(proposalLead),
            recipient_name: proposalLead.name || proposalLead.full_name || "",
            recipient_email: proposalLead.email || "",
          }}
          onClose={() => setProposalLead(null)}
          onCreated={(proposal) => {
            setProposalLead(null);
            navigate(`/prospecting/proposals/${proposal.id}`);
          }}
        />
      )}
    </div>
  );
};

export default SellerLeadsPage;
